import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bookmark, ChevronDown, ChevronUp, RotateCcw, Save, Pencil, Trash2, Filter, AlertTriangle,
} from 'lucide-react';
import {
  PRESET_VIEW_GROUPS, DEFAULT_ANALYTICS_FILTERS, filtersMatch,
} from '../../utils/analyticsFilters';
import Modal from '../common/Modal';
import './AnalyticsViewsPanel.css';

const SAVE_VIEW_FORM_ID = 'save-view-form';
const RENAME_VIEW_FORM_ID = 'rename-view-form';

function SaveViewModal({ open, onClose, onSave, saving, error }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) setName('');
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await onSave(name.trim());
      setName('');
      onClose();
    } catch {
      // error shown via prop
    }
  };

  return (
    <Modal
      title={t('analyticsViews.saveView')}
      onClose={onClose}
      size="sm"
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form={SAVE_VIEW_FORM_ID}
            className="btn btn-primary"
            disabled={!name.trim() || saving}
          >
            {saving ? <><span className="spinner spinner-sm" /> {t('common.saving')}</> : t('common.save')}
          </button>
        </>
      )}
    >
      <form id={SAVE_VIEW_FORM_ID} onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="save-view-name">
            {t('analyticsViews.viewNameLabel')}
          </label>
          <input
            id="save-view-name"
            className={`form-control ${error ? 'is-error' : ''}`}
            placeholder={t('analyticsViews.viewNamePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            autoFocus
            autoComplete="off"
          />
          {error && <p className="form-error">{error}</p>}
          <p className="form-hint">{t('analyticsViews.noSavedViews')}</p>
        </div>
      </form>
    </Modal>
  );
}

function RenameViewModal({ view, onClose, onRename, saving, error }) {
  const { t } = useTranslation();
  const [name, setName] = useState(view?.name || '');

  useEffect(() => {
    if (view) setName(view.name || '');
  }, [view]);

  if (!view) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === view.name) { onClose(); return; }
    try {
      await onRename(view.id, name.trim());
      onClose();
    } catch {
      // error shown via prop
    }
  };

  const unchanged = name.trim() === view.name;

  return (
    <Modal
      title={t('analyticsViews.renameView')}
      onClose={onClose}
      size="sm"
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form={RENAME_VIEW_FORM_ID}
            className="btn btn-primary"
            disabled={!name.trim() || unchanged || saving}
          >
            {saving ? <><span className="spinner spinner-sm" /> {t('common.saving')}</> : t('common.save')}
          </button>
        </>
      )}
    >
      <form id={RENAME_VIEW_FORM_ID} onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="rename-view-name">
            {t('analyticsViews.viewNameLabel')}
          </label>
          <input
            id="rename-view-name"
            className={`form-control ${error ? 'is-error' : ''}`}
            placeholder={t('analyticsViews.viewNamePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            autoFocus
            autoComplete="off"
          />
          {error && <p className="form-error">{error}</p>}
        </div>
      </form>
    </Modal>
  );
}

export default function AnalyticsViewsPanel({
  filters,
  onFiltersChange,
  onApplyPreset,
  onReset,
  activeView,
  filtersActive = false,
  savedViews,
  viewsLoading,
  onSaveView,
  onRenameView,
  onDeleteView,
  workers = [],
  rooms = [],
  categories = [],
  tags = [],
  staleFilterHints = [],
  resultCount,
  baseCount,
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [modalError, setModalError] = useState('');

  const isDefault = filtersMatch(filters, DEFAULT_ANALYTICS_FILTERS);
  const activeLabel = activeView?.label || activeView?.name || null;
  const showCustomBadge = filtersActive && !activeLabel;

  const patch = (partial) => onFiltersChange({ ...filters, ...partial });

  const handleSave = async (name) => {
    setSaving(true);
    setModalError('');
    try {
      await onSaveView(name, filters);
    } catch (err) {
      if (err?.code === 'DUPLICATE_NAME') {
        setModalError(t('analyticsViews.duplicateName'));
      }
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (id, name) => {
    setSaving(true);
    setModalError('');
    try {
      await onRenameView(id, name);
    } catch (err) {
      if (err?.code === 'DUPLICATE_NAME') {
        setModalError(t('analyticsViews.duplicateName'));
      }
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await onDeleteView(id);
    setConfirmDeleteId(null);
    if (activeView?.type === 'saved' && activeView.id === id) onReset();
  };

  const openSave = () => {
    setModalError('');
    setSaveOpen(true);
  };

  const openRename = (view) => {
    setModalError('');
    setRenameTarget(view);
  };

  return (
    <div className={`analytics-views-panel${expanded ? ' is-expanded' : ''}`}>
      <div className="av-toolbar">
        <button
          type="button"
          className="av-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <Bookmark size={15} />
          <span>{t('analyticsViews.title')}</span>
          {activeLabel && (
            <span className="av-active-badge" title={activeLabel}>{activeLabel}</span>
          )}
          {showCustomBadge && (
            <span className="av-custom-badge">{t('analyticsViews.customFilters')}</span>
          )}
          {filtersActive && resultCount != null && baseCount != null && !showAdvanced && (
            <span className="av-result-pill">
              {resultCount}/{baseCount}
            </span>
          )}
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        <div className="av-toolbar-actions">
          {!isDefault && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={onReset} title={t('analyticsViews.reset')}>
              <RotateCcw size={14} />
              <span className="av-btn-label">{t('analyticsViews.reset')}</span>
            </button>
          )}
          <button type="button" className="btn btn-sm btn-secondary" onClick={openSave}>
            <Save size={14} />
            <span className="av-btn-label">{t('analyticsViews.saveView')}</span>
          </button>
          <button
            type="button"
            className={`btn btn-sm ${showAdvanced ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setShowAdvanced((v) => !v)}
          >
            <Filter size={14} />
            <span className="av-btn-label">{t('analyticsViews.filters')}</span>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="av-body">
          {PRESET_VIEW_GROUPS.map((group) => (
            <div key={group.id} className="av-group">
              <span className="av-group-label">{t(group.labelKey)}</span>
              <div className="av-pills">
                {group.presets.map((preset) => {
                  const isActive = activeView?.type === 'preset' && activeView.id === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      className={`av-pill${isActive ? ' is-active' : ''}`}
                      onClick={() => onApplyPreset(preset.id)}
                      title={preset.hintKey ? t(preset.hintKey) : t(preset.labelKey)}
                    >
                      {t(preset.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {savedViews.length > 0 && (
            <div className="av-group">
              <span className="av-group-label">{t('analyticsViews.savedViews')}</span>
              <div className="av-saved-list">
                {savedViews.map((view) => {
                  const isActive = activeView?.type === 'saved' && activeView.id === view.id;
                  const lastUsed = view.lastUsedAt?.toDate?.() ?? (view.lastUsedAt ? new Date(view.lastUsedAt) : null);
                  return (
                    <div key={view.id} className={`av-saved-item${isActive ? ' is-active' : ''}`}>
                      <button
                        type="button"
                        className="av-saved-name"
                        onClick={() => onApplyPreset(`saved:${view.id}`, view)}
                        title={view.name}
                      >
                        {view.name}
                      </button>
                      <span className="av-saved-meta">
                        {view.createdByName}
                        {lastUsed && ` · ${lastUsed.toLocaleDateString()}`}
                      </span>
                      <div className="av-saved-actions">
                        <button
                          type="button"
                          className="btn btn-icon"
                          title={t('analyticsViews.renameView')}
                          onClick={() => openRename(view)}
                        >
                          <Pencil size={13} />
                        </button>
                        {confirmDeleteId === view.id ? (
                          <>
                            <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(view.id)}>
                              {t('common.confirm')}
                            </button>
                            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirmDeleteId(null)}>
                              {t('common.cancel')}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-icon"
                            title={t('analyticsViews.deleteView')}
                            onClick={() => setConfirmDeleteId(view.id)}
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {viewsLoading && (
            <p className="av-empty-hint text-muted text-sm">{t('analyticsViews.loading')}</p>
          )}

          {!viewsLoading && savedViews.length === 0 && (
            <p className="av-empty-hint text-muted text-sm">{t('analyticsViews.noSavedViews')}</p>
          )}
        </div>
      )}

      {showAdvanced && (
        <div className="av-advanced">
          <div className="av-advanced-header">
            <div className="av-advanced-title">
              <h3>{t('analyticsViews.filters')}</h3>
              {resultCount != null && baseCount != null && (
                <p className="av-advanced-subtitle">
                  {t('analyticsViews.showingTasks', { count: resultCount, total: baseCount })}
                </p>
              )}
            </div>
            {!isDefault && (
              <div className="av-advanced-header-actions">
                <button type="button" className="btn btn-sm btn-ghost" onClick={onReset}>
                  <RotateCcw size={14} />
                  <span className="av-btn-label">{t('analyticsViews.clearFilters')}</span>
                </button>
              </div>
            )}
          </div>

          {staleFilterHints.length > 0 && (
            <div className="av-stale-warning" style={{ marginBottom: 'var(--space-4)' }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{t('analyticsViews.staleFilterHint')}</span>
            </div>
          )}

          <div className="av-filter-sections">
            <section className="av-filter-section" aria-label={t('analyticsViews.filterSections.dimensions')}>
              <h4 className="av-section-title">{t('analyticsViews.filterSections.dimensions')}</h4>
              <div className="av-section-grid">
                <label className="av-field">
                  <span className="av-field-label">{t('analyticsViews.fields.worker')}</span>
                  <select
                    className="av-field-control"
                    value={filters.workerName}
                    onChange={(e) => patch({ workerName: e.target.value })}
                  >
                    <option value="">{t('common.all')}</option>
                    {workers.map((w) => (
                      <option key={w} value={w}>{w}</option>
                    ))}
                  </select>
                </label>
                <label className="av-field">
                  <span className="av-field-label">{t('analyticsViews.fields.room')}</span>
                  <select
                    className="av-field-control"
                    value={filters.roomId}
                    onChange={(e) => patch({ roomId: e.target.value })}
                  >
                    <option value="">{t('common.all')}</option>
                    {rooms.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </label>
                <label className="av-field">
                  <span className="av-field-label">{t('analyticsViews.fields.category')}</span>
                  <select
                    className="av-field-control"
                    value={filters.taskCategoryId}
                    onChange={(e) => patch({ taskCategoryId: e.target.value })}
                  >
                    <option value="">{t('common.all')}</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </label>
                <label className="av-field">
                  <span className="av-field-label">{t('analyticsViews.fields.tag')}</span>
                  <select
                    className="av-field-control"
                    value={filters.tagId}
                    onChange={(e) => patch({ tagId: e.target.value })}
                  >
                    <option value="">{t('common.all')}</option>
                    {tags.map((tg) => (
                      <option key={tg.id} value={tg.id}>{tg.name}</option>
                    ))}
                  </select>
                </label>
                <label className="av-field">
                  <span className="av-field-label">{t('analyticsViews.fields.taskType')}</span>
                  <select
                    className="av-field-control"
                    value={filters.taskType}
                    onChange={(e) => patch({ taskType: e.target.value })}
                  >
                    <option value="all">{t('common.all')}</option>
                    <option value="production">Production</option>
                    <option value="support">Support</option>
                    <option value="delay">Delay</option>
                  </select>
                </label>
                <label className="av-field">
                  <span className="av-field-label">{t('analyticsViews.fields.shift')}</span>
                  <select
                    className="av-field-control"
                    value={filters.shift}
                    onChange={(e) => patch({ shift: e.target.value })}
                  >
                    <option value="">{t('common.all')}</option>
                    <option value="morning">{t('analyticsViews.shifts.morning')}</option>
                    <option value="afternoon">{t('analyticsViews.shifts.afternoon')}</option>
                    <option value="evening">{t('analyticsViews.shifts.evening')}</option>
                    <option value="night">{t('analyticsViews.shifts.night')}</option>
                  </select>
                </label>
              </div>
            </section>

            <section className="av-filter-section" aria-label={t('analyticsViews.filterSections.product')}>
              <h4 className="av-section-title">{t('analyticsViews.filterSections.product')}</h4>
              <div className="av-section-grid av-section-grid--2">
                <label className="av-field">
                  <span className="av-field-label">{t('analyticsViews.fields.product')}</span>
                  <input
                    className="av-field-control"
                    placeholder={t('analyticsViews.productPlaceholder')}
                    value={filters.productName}
                    onChange={(e) => patch({ productName: e.target.value })}
                  />
                </label>
              </div>
            </section>

            <section className="av-filter-section" aria-label={t('analyticsViews.filterSections.ranges')}>
              <h4 className="av-section-title">{t('analyticsViews.filterSections.ranges')}</h4>
              <div className="av-range-group">
                <div className="av-range-pair">
                  <span className="av-range-pair-label">{t('admin.cost')}</span>
                  <div className="av-range-inputs">
                    <label className="av-range-field">
                      <span>{t('analyticsViews.rangeMin')}</span>
                      <input
                        type="number"
                        className="av-field-control"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        value={filters.costMin ?? ''}
                        onChange={(e) => patch({ costMin: e.target.value === '' ? null : Number(e.target.value) })}
                      />
                    </label>
                    <label className="av-range-field">
                      <span>{t('analyticsViews.rangeMax')}</span>
                      <input
                        type="number"
                        className="av-field-control"
                        min="0"
                        step="0.01"
                        placeholder="—"
                        value={filters.costMax ?? ''}
                        onChange={(e) => patch({ costMax: e.target.value === '' ? null : Number(e.target.value) })}
                      />
                    </label>
                  </div>
                </div>
                <div className="av-range-pair">
                  <span className="av-range-pair-label">{t('analyticsViews.fields.duration')}</span>
                  <div className="av-range-inputs">
                    <label className="av-range-field">
                      <span>{t('analyticsViews.rangeMin')}</span>
                      <input
                        type="number"
                        className="av-field-control"
                        min="0"
                        placeholder="0"
                        value={filters.durationMin ?? ''}
                        onChange={(e) => patch({ durationMin: e.target.value === '' ? null : Number(e.target.value) })}
                      />
                    </label>
                    <label className="av-range-field">
                      <span>{t('analyticsViews.rangeMax')}</span>
                      <input
                        type="number"
                        className="av-field-control"
                        min="0"
                        placeholder="—"
                        value={filters.durationMax ?? ''}
                        onChange={(e) => patch({ durationMax: e.target.value === '' ? null : Number(e.target.value) })}
                      />
                    </label>
                  </div>
                </div>
              </div>
            </section>

            <section className="av-filter-section" aria-label={t('analyticsViews.filterSections.quality')}>
              <h4 className="av-section-title">{t('analyticsViews.filterSections.quality')}</h4>
              <div className="av-quality-row">
                <label className="av-checkbox">
                  <input
                    type="checkbox"
                    checked={filters.hasIssueNotes}
                    onChange={(e) => patch({ hasIssueNotes: e.target.checked })}
                  />
                  <span>{t('analyticsViews.fields.hasIssueNotes')}</span>
                </label>
              </div>
            </section>
          </div>
        </div>
      )}

      <SaveViewModal
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        onSave={handleSave}
        saving={saving}
        error={modalError}
      />
      <RenameViewModal
        view={renameTarget}
        onClose={() => setRenameTarget(null)}
        onRename={handleRename}
        saving={saving}
        error={modalError}
      />
    </div>
  );
}
